<template>
    <form
        @change="isFormDirty"
        @submit.prevent="handleSubmit"
        :class="{ 'form-container': typeForm, 'form-modal-container': typeModal }"
        data-cy="form-container"
    >
        <p class="text-center text-default-red p-2" v-if="validation.message">
            {{ validation.message }}
        </p>
        <div class="form-section" data-cy="section-general">
            <div class="section-body">
                <div class="input-container w-full px-4 mb-4" data-cy="field-association">
                    <ui-label>{{ $t('admin.list_manager.form.labels.association') }}</ui-label>
                    <ui-select v-model="v$.list_manager_item.association_id.$model" :options="association_list" />
                    <validation-errors :errors="validation.errors.association_id" v-if="validation.errors" />
                </div>
                <div class="input-container w-full px-4 mb-4" data-cy="field-company">
                    <ui-label>{{ $t('admin.list_manager.form.labels.company') }}</ui-label>
                    <ui-select v-model="v$.list_manager_item.company_id.$model" :options="companies" />
                    <span class="text-help">{{ $t('admin.list_manager.form.help.company') }}</span>
                    <validation-errors :errors="validation.errors.company_id" v-if="validation.errors" />
                </div>
                <div data-cy="field-category" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ meta_display.category }}</ui-label>
                    <ui-select
                        :class="{ 'form-error': v$.list_manager_item.category.$error }"
                        v-model="v$.list_manager_item.category.$model"
                        :options="categories"
                        v-if="list_manager_item.id == undefined"
                        @updatedValue="setupMeta"
                    />
                    <div class="subText pt-2" v-if="list_manager_item.id">{{ list_manager_item.category }}</div>
                    <div
                        data-cy="field-category-error"
                        :class="{ 'text-default-red': v$.list_manager_item.category.$error }"
                        v-if="v$.list_manager_item.category.required.$invalid && v$.list_manager_item.category.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors :errors="validation.errors.category" v-if="validation.errors" />
                </div>
                <div data-cy="field-item" class="input-container w-1/2 px-4 mb-4">
                    <ui-label>{{ meta_display.item }}</ui-label>
                    <ui-input
                        :class="{ 'form-error': v$.list_manager_item.item.$error }"
                        v-model="v$.list_manager_item.item.$model"
                    />
                    <validation-errors :errors="validation.errors.item" v-if="validation.errors" />
                </div>
                <div data-cy="field-value" class="input-container w-1/2 px-4 mb-4">
                    <ui-label>{{ meta_display.value }}</ui-label>
                    <ui-input
                        :class="{ 'form-error': v$.list_manager_item.value.$error }"
                        v-model="v$.list_manager_item.value.$model"
                    />
                    <validation-errors :errors="validation.errors.value" v-if="validation.errors" />
                </div>
                <div data-cy="field-description" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ meta_display.description }}</ui-label>
                    <ui-text-area
                        :class="{ 'form-error': v$.list_manager_item.description.$error }"
                        v-model="v$.list_manager_item.description.$model"
                    />
                    <validation-errors :errors="validation.errors.description" v-if="validation.errors" />
                </div>
                <div data-cy="field-option-a" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ meta_display.option_a }}</ui-label>
                    <ui-input
                        :class="{ 'form-error': v$.list_manager_item.option_a.$error }"
                        v-model="v$.list_manager_item.option_a.$model"
                    />
                    <validation-errors :errors="validation.errors.option_a" v-if="validation.errors" />
                </div>
                <div data-cy="field-option-b" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ meta_display.option_b }}</ui-label>
                    <ui-input
                        :class="{ 'form-error': v$.list_manager_item.option_b.$error }"
                        v-model="v$.list_manager_item.option_b.$model"
                    />
                    <validation-errors :errors="validation.errors.option_b" v-if="validation.errors" />
                </div>
                <div data-cy="field-option-c" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ meta_display.option_c }}</ui-label>
                    <ui-input
                        :class="{ 'form-error': v$.list_manager_item.option_c.$error }"
                        v-model="v$.list_manager_item.option_c.$model"
                    />
                    <validation-errors :errors="validation.errors.option_c" v-if="validation.errors" />
                </div>
                <div data-cy="field-flag-a" class="input-container w-1/3 px-4">
                    <ui-checkbox v-model="list_manager_item.flag_a">
                        {{ meta_display.flag_a }}
                    </ui-checkbox>
                </div>
                <div data-cy="field-flag-b" class="input-container w-1/3 px-4">
                    <ui-checkbox v-model="list_manager_item.flag_b">
                        {{ meta_display.flag_b }}
                    </ui-checkbox>
                </div>
                <div data-cy="field-flag-c" class="input-container w-1/3 px-4">
                    <ui-checkbox v-model="list_manager_item.flag_c">
                        {{ meta_display.flag_c }}
                    </ui-checkbox>
                </div>
                <div data-cy="field-notes" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ meta_display.notes }}</ui-label>
                    <ui-text-area
                        :class="{ 'form-error': v$.list_manager_item.notes.$error }"
                        v-model="v$.list_manager_item.notes.$model"
                    />
                    <validation-errors :errors="validation.errors.notes" v-if="validation.errors" />
                </div>
            </div>
        </div>

        <div class="form-section" data-cy="section-flags">
            <div class="section-body">
                <div data-cy="field-is-active" class="input-container lg:w-1/3 px-4">
                    <ui-checkbox v-model="list_manager_item.is_active">
                        {{ meta_display.is_active }}
                    </ui-checkbox>
                </div>
            </div>
        </div>

        <div class="form-submit" v-if="form_type == 'form'">
            <ui-submit-button data-cy="submit-button" :disabled="processing">
                <span v-if="!processing">{{ $t('admin.list_manager.form.buttons.save') }}</span>
                <span v-else>{{ $t('admin.list_manager.form.buttons.saving') }}</span>
            </ui-submit-button>
        </div>

        <div class="drawer-footer" v-if="form_type == 'drawer'">
            <a-button data-cy="drawer-cancel-btn" class="btn-cancel" @click="onDrawerClose">
                {{ $t('admin.list_manager.form.buttons.cancel') }}
            </a-button>
            <a-button data-cy="drawer-save-btn" class="ant-btn-primary" :disabled="processing" @click="handleSubmit">
                <span v-if="!processing">{{ $t('admin.list_manager.form.buttons.save') }}</span>
                <span v-else>{{ $t('admin.list_manager.form.buttons.saving') }}</span>
            </a-button>
        </div>
    </form>
</template>

<script>
import UiCheckbox from '@/components/ui/Checkbox.vue';
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSelect from '@/components/ui/Select.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';
import UiTextArea from '@/components/ui/TextArea.vue';

import ValidationErrors from '@/components/ui/ValidationErrors.vue';

import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiCheckbox,
        UiInput,
        UiTextArea,
        UiLabel,
        UiSelect,
        UiSubmitButton,
        ValidationErrors,
    },
    data() {
        return {
            categories: [],
            association_list: [],
            company_list: [],
            meta_display: {},
        };
    },
    validations: {
        list_manager_item: {
            association_id: {},
            company_id: {},
            category: {
                required,
            },
            item: {},
            value: {},
            description: {},
            flag_a: {},
            flag_b: {},
            flag_c: {},
            option_a: {},
            option_b: {},
            option_c: {},
            notes: {},
            is_active: {},
        },
    },
    mounted() {
        this.setupMeta();
        this.setupAssociationList();
        this.setupCompanyList();
        this.fetch();
    },
    computed: {
        typeForm() {
            return this.form_type !== 'modal';
        },
        typeModal() {
            return this.form_type == 'modal';
        },
        companies() {
            let company_filter = [];

            if (this.list_manager_item.association_id != null && this.list_manager_item.association_id != '') {
                this.company_list.forEach((record) => {
                    if (this.list_manager_item.association_id == record.association_id) {
                        company_filter.push({
                            text: record.company_name,
                            value: record.company_id,
                        });
                    }
                });

                let match = false;

                if (
                    this.list_manager_item.company_id != null &&
                    this.list_manager_item.company_id != '' &&
                    company_filter.length > 0
                ) {
                    company_filter.forEach((record) => {
                        if (this.list_manager_item.company_id == record.value) {
                            match = true;
                        }
                    });

                    if (!match) {
                        this.list_manager_item.company_id = null;
                    }
                }
            } else {
                this.company_list.forEach((record) => {
                    company_filter.push({
                        text: record.company_name,
                        value: record.company_id,
                    });
                });
            }

            return company_filter;
        },
    },
    props: {
        form_type: {
            type: String,
            default: 'drawer',
        },
        list_manager_item: Object,
        processing: {
            type: Boolean,
            default: false,
        },
        validation: Object,
    },
    methods: {
        setupMeta() {
            if (this.list_manager_item.category !== '') {
                this.$http.get('/api/listmanagermeta/' + this.list_manager_item.category).then((response) => {
                    let meta_data = {
                        ...response.data.data,
                    };

                    let meta_display = {
                        category: this.$t('admin.list_manager.form.labels.category'),
                        item: meta_data.item ? meta_data.item : this.$t('admin.list_manager.form.labels.item'),
                        value: meta_data.value ? meta_data.value : this.$t('admin.list_manager.form.labels.value'),
                        description: meta_data.description
                            ? meta_data.description
                            : this.$t('admin.list_manager.form.labels.description'),
                        option_a: meta_data.option_a
                            ? meta_data.option_a
                            : this.$t('admin.list_manager.form.labels.option_a'),
                        option_b: meta_data.option_b
                            ? meta_data.option_b
                            : this.$t('admin.list_manager.form.labels.option_b'),
                        option_c: meta_data.option_c
                            ? meta_data.option_c
                            : this.$t('admin.list_manager.form.labels.option_c'),
                        flag_a: meta_data.flag_a ? meta_data.flag_a : this.$t('admin.list_manager.form.labels.flag_a'),
                        flag_b: meta_data.flag_b ? meta_data.flag_b : this.$t('admin.list_manager.form.labels.flag_b'),
                        flag_c: meta_data.flag_c ? meta_data.flag_c : this.$t('admin.list_manager.form.labels.flag_c'),
                        notes: meta_data.notes ? meta_data.notes : this.$t('admin.list_manager.form.labels.notes'),
                        is_active: this.$t('admin.list_manager.form.labels.is_active'),
                    };

                    this.meta = meta_data;
                    this.meta_display = meta_display;
                });
            } else {
                this.meta_display = {
                    category: this.$t('admin.list_manager.form.labels.category'),
                    item: this.$t('admin.list_manager.form.labels.item'),
                    value: this.$t('admin.list_manager.form.labels.value'),
                    description: this.$t('admin.list_manager.form.labels.description'),
                    option_a: this.$t('admin.list_manager.form.labels.option_a'),
                    option_b: this.$t('admin.list_manager.form.labels.option_b'),
                    option_c: this.$t('admin.list_manager.form.labels.option_c'),
                    flag_a: this.$t('admin.list_manager.form.labels.flag_a'),
                    flag_b: this.$t('admin.list_manager.form.labels.flag_b'),
                    flag_c: this.$t('admin.list_manager.form.labels.flag_c'),
                    notes: this.$t('admin.list_manager.form.labels.notes'),
                    is_active: this.$t('admin.list_manager.form.labels.is_active'),
                };
            }
        },
        setupAssociationList() {
            this.$http.get('/api/admin/list-associations').then((response) => {
                this.association_list = [];

                response.data.data.forEach((record) => {
                    this.association_list.push({
                        text: record.name,
                        value: record.id,
                    });
                });
            });
        },
        setupCompanyList() {
            this.$http.get('/api/admin/list-companies-by-associations').then((response) => {
                this.company_list = response.data.data;
            });
        },
        fetch() {
            this.$http.get('/api/listmanagermeta/categories').then((response) => {
                this.categories = [];

                response.data.data.forEach((record) => {
                    this.categories.push({
                        text: record.list_name,
                        value: record.category,
                    });
                });
            });
        },
        isFormDirty() {
            this.$emit('is-form-dirty', this.v$.$anyDirty);
        },
        handleFilterChange(value) {
            if (value == undefined) {
                this.list_manager_item.association_id = null;
            } else {
                this.list_manager_item.association_id = this.association_list[value].id;
            }
        },
        async handleSubmit() {
            if (!(await this.v$.$validate())) return false;

            this.$emit('formSubmit', this.list_manager_item);
        },
        onDrawerClose() {
            this.$emit('onDrawerClose');
        },
    },
};
</script>
